/**
 * TopNav — Shared top navigation bar
 *
 * Fixed glassmorphism nav with brand wordmark, navigation links,
 * auth-conditional controls, and responsive hamburger menu.
 *
 * Restyled to consume the Meridian/Reignara design tokens:
 *  - background/border/text use semantic tokens (`bg-background`,
 *    `border-border`, `text-foreground`, `text-muted-foreground`).
 *  - primary brand wordmark uses `font-display` (Bavex); body / nav copy
 *    stays on `font-sans` (Inter).
 *  - active-nav underline / active-state color uses `--primary`
 *    (`text-primary`, `bg-primary`, `border-primary`).
 *
 * The nav-items array, link list, mobile menu logic, and routing are
 * intentionally unchanged (Requirement 17.1).
 *
 * Spec: meridian-redesign
 * Requirements: 5.1, 5.2, 5.4, 17.1
 *
 * demo-tenant-environment Task 24 — Requirements 13.1, 13.2, 13.4
 * ---------------------------------------------------------------
 * The wordmark used to be the literal "Idaho Pageants" with the literal tagline
 * "The Gem State Legacy". Both are gone. The NAME is the active tenant's resolved
 * brand name from `BrandingConfig` (Req 1.1 puts the displayed tenant name in that
 * record, and this nav is the most brand-chrome surface in the app); the TAGLINE
 * is the tenant's own marketing copy, and a tenant without one renders no tagline
 * rather than another tenant's.
 */

import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTenantBrandName, useTenantCopy } from '@/hooks/useTenantCopy';
import { Menu, X, ChevronDown, Crown } from 'lucide-react';

export interface TopNavProps {
  /** Currently active route path for link highlighting */
  activePath?: string;
}

export function TopNav({ activePath }: TopNavProps) {
  const { isAuthenticated, isDirector, signOut } = useAuth();
  // Task 24: brand identity from `BrandingConfig`, tagline from tenant copy.
  const brandName = useTenantBrandName();
  const { marketing } = useTenantCopy();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const aboutDropdownRef = useRef<HTMLDivElement>(null);

  const currentPath = activePath ?? location.pathname;

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about', hasDropdown: true },
    { label: 'Apply', path: '/apply' },
  ];

  const aboutSubLinks = [
    { label: 'General Information', path: '/about' },
    { label: 'Our Titleholders', path: '/about/titleholders' },
    { label: 'Our Team', path: '/about/team' },
    { label: 'Our Regulations', path: '/about/regulations' },
    { label: 'FAQs', path: '/about/faqs' },
    { label: 'Contact Us', path: '/about/contact' },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (aboutDropdownRef.current && !aboutDropdownRef.current.contains(event.target as Node)) {
        setAboutDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    try {
      window.sessionStorage.clear();
    } catch {
      // Ignore storage access errors
    }
  };

  return (
    <nav
      data-testid="top-nav"
      className="fixed top-0 left-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-border"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">

          {/* Brand wordmark */}
          <Link to="/" className="flex items-center gap-2 group">
            <Crown className="w-8 h-8 text-primary" />
            <div className="flex flex-col">
              <span
                data-testid="top-nav-wordmark"
                className="font-display text-xl tracking-widest uppercase leading-none text-foreground"
              >
                {brandName}
              </span>
              {marketing.tagline && (
                <span
                  data-testid="top-nav-tagline"
                  className="font-sans text-[10px] tracking-[0.3em] uppercase text-muted-foreground"
                >
                  {marketing.tagline}
                </span>
              )}
            </div>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-12">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div
                  key={link.path}
                  ref={aboutDropdownRef}
                  className="relative flex items-center h-full"
                  onMouseEnter={() => setAboutDropdownOpen(true)}
                  onMouseLeave={() => setAboutDropdownOpen(false)}
                >
                  <Link
                    to={link.path}
                    className={`font-sans text-xs uppercase tracking-widest nav-item-hover py-1 flex items-center gap-1 touch-target ${
                      currentPath.startsWith(link.path) ? 'text-primary' : 'text-foreground'
                    }`}
                  >
                    {link.label}
                    <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${aboutDropdownOpen ? 'rotate-180' : ''}`} />
                  </Link>

                  {/* About dropdown menu */}
                  <div
                    data-testid="about-dropdown"
                    className={`absolute top-full left-0 w-56 bg-card border border-border shadow-prominent transition-all duration-300 overflow-hidden ${
                      aboutDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                    }`}
                  >
                    {aboutSubLinks.map((sub) => (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        onClick={() => setAboutDropdownOpen(false)}
                        className="block px-6 py-4 min-h-[var(--touch-target-min)] text-[10px] uppercase tracking-widest text-foreground hover:bg-muted hover:text-primary transition-colors border-b border-border last:border-0"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-sans text-xs uppercase tracking-widest nav-item-hover py-1 touch-target ${
                    currentPath === link.path ? 'text-primary' : 'text-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}

            {/* Auth-conditional controls */}
            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                <Link
                  to={isDirector ? '/admin' : '/portal'}
                  className="bg-primary text-primary-foreground px-8 py-3 min-h-[var(--touch-target-min)] inline-flex items-center text-eyebrow-sm hover:bg-primary/90 transition-colors duration-300 rounded-input"
                  data-testid="profile-link"
                >
                  {isDirector ? 'Admin Portal' : 'Portal'}
                </Link>
                <button
                  onClick={handleSignOut}
                  data-testid="sign-out-button"
                  className="font-sans text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors duration-300 ease-out cursor-pointer bg-transparent border-none touch-target"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Link
                  to="/signin"
                  className="font-sans text-xs uppercase tracking-widest text-foreground hover:text-primary transition-colors duration-300 ease-out touch-target"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="font-sans text-xs uppercase tracking-widest bg-primary text-primary-foreground px-6 py-2.5 min-h-[var(--touch-target-min)] inline-flex items-center hover:bg-primary/90 transition-colors duration-300 ease-out rounded-input"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile hamburger toggle */}
          <button
            className="md:hidden cursor-pointer bg-transparent border-none touch-target"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? (
              <X className="h-6 w-6 text-foreground" />
            ) : (
              <Menu className="h-6 w-6 text-foreground" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background border-b border-border px-6 py-8 flex flex-col gap-6 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          {navLinks.map((link) => (
            <div key={link.path} className="flex flex-col gap-4">
              {link.hasDropdown ? (
                <>
                  <button
                    onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
                    className="flex justify-between items-center min-h-[var(--touch-target-min)] font-sans text-sm uppercase tracking-widest text-foreground bg-transparent border-none cursor-pointer p-0 text-left"
                    data-testid="mobile-about-toggle"
                  >
                    {link.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileAboutOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileAboutOpen && (
                    <div className="flex flex-col gap-4 pl-4 border-l border-border">
                      {aboutSubLinks.map((sub) => (
                        <Link
                          key={sub.path}
                          to={sub.path}
                          onClick={() => setMobileOpen(false)}
                          className="font-sans text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors min-h-[var(--touch-target-min)] flex items-center"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  className="font-sans text-sm uppercase tracking-widest text-foreground min-h-[var(--touch-target-min)] flex items-center"
                >
                  {link.label}
                </Link>
              )}
            </div>
          ))}

          {isAuthenticated ? (
            <>
              <Link
                to={isDirector ? '/admin' : '/portal'}
                onClick={() => setMobileOpen(false)}
                className="bg-primary text-primary-foreground px-8 py-4 min-h-[var(--touch-target-min)] flex items-center justify-center text-center font-sans text-xs uppercase tracking-[0.2em] rounded-input"
              >
                {isDirector ? 'Admin Portal' : 'Portal'}
              </Link>
              <button
                onClick={() => {
                  handleSignOut();
                  setMobileOpen(false);
                }}
                data-testid="mobile-sign-out-button"
                className="font-sans text-sm uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors duration-300 ease-out text-left cursor-pointer bg-transparent border-none min-h-[var(--touch-target-min)] flex items-center"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link
                to="/signin"
                onClick={() => setMobileOpen(false)}
                className="font-sans text-sm uppercase tracking-widest text-foreground min-h-[var(--touch-target-min)] flex items-center"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                onClick={() => setMobileOpen(false)}
                className="bg-primary text-primary-foreground px-8 py-4 min-h-[var(--touch-target-min)] flex items-center justify-center text-center font-sans text-xs uppercase tracking-[0.2em] rounded-input"
              >
                Sign Up
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
